/**
 * Overwrite protection for uploads and downloads.
 *
 * Until this existed, every transfer replaced a same-named file silently:
 * `sftp.create` and `File::create` both truncate. Uploading `index.html` onto a
 * server that already had one destroyed the old one with no prompt at all.
 *
 * # The decision the owner described, and one deliberate difference
 *
 * Replace / Skip per clashing file, plus Replace all / Skip all for the rest of
 * the batch — the classic FTP-client dialog. The difference: **every question
 * is asked BEFORE any transfer starts**, rather than pausing the queue when it
 * reaches a clashing file. On a ten-file upload where file seven clashes, a
 * mid-queue pause means coming back ten minutes later to find it waiting; asking
 * up front means answering once and walking away.
 *
 * Pure over its inputs — the prompt is injected — so every branch is executed
 * in tests rather than reasoned about.
 */

/**
 * @param {object} args
 * @param {Array<{name: string}>} args.items    the batch, in order
 * @param {Map<string, boolean>} args.existing  name → isDirectory, at the destination
 * @param {(q: {name: string, remaining: number}) => Promise<{action: string, all?: boolean}>} args.ask
 * @returns {Promise<{toStart: object[], skipped: string[], blocked: string[], cancelled?: boolean}>}
 */
export async function planTransfers({ items, existing, ask }) {
  const toStart = [];
  const skipped = [];
  // A FOLDER with the same name as the file: no answer the user could give
  // makes that a replace — a file cannot overwrite a directory — so it is not
  // asked, only reported.
  const blocked = [];
  const clashing = [];

  for (const item of items || []) {
    if (!existing.has(item.name)) toStart.push(item);
    else if (existing.get(item.name)) blocked.push(item.name);
    else clashing.push(item);
  }

  // Clashes are asked in batch order, and a "… all" answer applies to every
  // clash AFTER it in this batch — never to a later, separate batch.
  const decided = [];
  let policy = null;
  for (let i = 0; i < clashing.length; i += 1) {
    const item = clashing[i];
    let action = policy;
    if (!action) {
      // eslint-disable-next-line no-await-in-loop
      const answer = await ask({ name: item.name, remaining: clashing.length - i });
      // Cancel (offered only by a folder merge): stop asking and start NOTHING
      // — not even what was already approved, so the batch leaves no trace.
      if (answer && answer.action === 'cancel') {
        return { toStart: [], skipped: (items || []).map((x) => x.name), blocked, cancelled: true };
      }
      // **Anything but an explicit "replace" is a skip**: closing the dialog,
      // pressing Escape, or a prompt torn down without an answer must never
      // destroy a file.
      action = answer && answer.action === 'replace' ? 'replace' : 'skip';
      if (answer && answer.all) policy = action;
    }
    if (action === 'replace') decided.push(item);
    else skipped.push(item.name);
  }

  // Original batch order is kept for what does start, so a replaced file does
  // not jump the queue ahead of the untouched ones.
  const order = new Map((items || []).map((item, index) => [item, index]));
  const all = [...toStart, ...decided].sort((a, b) => order.get(a) - order.get(b));
  return { toStart: all, skipped, blocked };
}

/** Destination listing → the `existing` map `planTransfers` wants. */
export function existingNames(entries) {
  return new Map((entries || []).map((e) => [e.name, !!e.isDir]));
}
